import { type ComponentProps, Suspense, lazy, useMemo, useState } from "react";

import { useDataProvider } from "@refinedev/core";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  Pencil,
  RotateCcw,
  ThumbsUp,
  X,
} from "lucide-react";

import { enrichmentConfigFor } from "@/components/enrichment/config";
import {
  EnrichmentSection,
  FeatureScores,
  ReasoningBlock,
  SeoGeoBlock,
  TransliterationsBlock,
} from "@/components/show/enrichment";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  MOVIE_FEATURE_LABELS,
  PERSON_FEATURE_LABELS,
  SHOW_GENRE_FEATURE_LABELS,
} from "@/lib/enrichment";
import {
  ENRICHMENT_PARK_LIMIT,
  type EnrichmentEntity,
  enrichmentEntityLabel,
  enrichmentProblemOf,
  isEnrichmentEntity,
  isLastAttempt,
  parkAttemptLabel,
} from "@/lib/tina/enrichment-jobs";
import { asRecord, asString } from "@/lib/tina/value-readers";
import {
  type ArtistEnrichmentRead,
  artistEnrichmentReadSchema,
} from "@/pages/artists/schemas/artist.aienrichment.form";
// The enrichment surface's resource name, shared with the force-close path in
// `hitl-panel.tsx` — `queue-return.ts` now owns the whole "which queue did this park
// come from" question, because `refresh_review` needed the same answer.
import { ENRICHMENT_RESOURCE } from "@/pages/tina/items/components/panels/hitl-panel/queue-return";
import type { ResolutionComponentProps } from "@/pages/tina/items/components/panels/hitl-panel/resolution-components";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import {
  type VenueEnrichmentRead,
  venueEnrichmentReadSchema,
} from "@/pages/venues/schemas/venue.aienrichment.form";

/**
 * The three editors are `lazy`, and that is load-bearing, not an optimisation.
 *
 * `resolution-components.tsx` is the registry every HITL panel imports, so
 * anything statically reachable from this module joins the import graph of
 * every test that touches the registry. `EnrichmentEditor` and
 * `VenueEnrichmentResult` pull AutoForm and `photo-zoomable` → `imgUtils` →
 * `firebaseConfig`, which calls `getRemoteConfig()` at module scope and throws
 * in jsdom; `JsonEnrichmentEditor` pulls all of Monaco. Deferring them keeps
 * the registry cheap to import and keeps ~1 MB of editor out of the entry
 * chunk, since a curator reaches them only by clicking "Edit & approve".
 */
const EnrichmentEditor = lazy(
  () => import("@/components/enrichment/enrichment-editor"),
);
const VenueEnrichmentResult = lazy(() =>
  import("@/pages/venues/create/components/enrichment-result").then((mod) => ({
    default: mod.VenueEnrichmentResult,
  })),
);
const JsonEnrichmentEditor = lazy(() =>
  import("@/components/enrichment/json-enrichment-editor").then((mod) => ({
    default: mod.JsonEnrichmentEditor,
  })),
);

/**
 * The Mood resource behind each enrichment entity — the one read this panel
 * does of the *entity* rather than of the job row (see
 * {@link useEntityAlreadyEnriched}).
 *
 * Total, not `Partial`, even though only six of the eleven can park today: the
 * guard it feeds is a safety guard, and one that silently stopped applying to a
 * twelfth gated flow would be worse than not having it. A new member of
 * `EnrichmentEntity` is a compile error here. Of the two MOO-4814 task 17
 * additions, `performance` is ungated exactly like `screening`; `play` IS
 * gated (0.5, movie's threshold) and joins the JSON-fallback branch in
 * {@link useEditorChoice} below — either way, the guard still needs somewhere
 * to read the entity's `enrichmentStatus` from.
 */
const ENTITY_RESOURCE: Record<EnrichmentEntity, string> = {
  artist: "artists",
  venue: "venues",
  event: "events",
  genre: "genres",
  geodata: "geodata",
  movie: "movies",
  person: "people",
  screening: "screenings",
  play: "plays",
  performance: "performances",
  show_genre: "show-genres",
};

/**
 * The three actions on a parked enrichment proposal.
 *
 * ```
 * [ Approve proposal ]  [ Edit & approve ]  [ Re-run enrichment ]
 * ```
 *
 * All three go through the existing `resolveHitl` — there is **no new write
 * path and no proposal fetch**. The whole proposal rides inline on
 * `hitl_context.problem.proposal`, so `GET /hitl/{id}/proposal` does not exist
 * and is not coming.
 *
 * "Edit & approve" reuses the entity's *existing* editors rather than growing a
 * second one: both `EnrichmentEditor` and `VenueEnrichmentResult` hold no
 * persistence of their own and keep edits in the parent's state until the
 * parent saves. Here the parent is this component and "Approve & Save" is the
 * `replace` resolve.
 */
export function EnrichmentResolution({
  item,
  hitlContext,
  onResolved,
}: ResolutionComponentProps) {
  const { resolveHitl } = useItemsRepository();
  const problem = enrichmentProblemOf(hitlContext);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [jsonValid, setJsonValid] = useState(true);

  const payload = useMemo<Record<string, unknown>>(
    () => problem?.proposal?.payload ?? {},
    [problem],
  );
  const hasProposal = Object.keys(payload).length > 0;
  // `null` means "the curator has not touched it", which is a different thing
  // from "the curator emptied it" — the JSON editor can legitimately produce
  // `{}`, and submitting the untouched-vs-emptied cases identically would let a
  // stray click wipe an enrichment.
  const [overlay, setOverlay] = useState<Record<string, unknown> | null>(null);

  const entity = problem?.entity_type ?? null;
  const editor = useEditorChoice(entity, payload, hasProposal);
  const guard = useEntityAlreadyEnriched(entity, problem?.entity_id ?? null);

  // What "Approve & save edits" would actually PUT — computed once, so the
  // button's enabled state and the submit cannot disagree about it.
  //
  // The typed review dialogs merge shallowly and hand back the whole
  // enrichment, so the overlay is already the proposal with the curator's edits
  // on top — spreading over `payload` is belt-and-braces against the lenient
  // read schema having dropped a key. The JSON editor is taken verbatim:
  // there, deleting a key is a deliberate edit, and re-spreading the original
  // would silently resurrect it.
  const editedEnrichment = useMemo<Record<string, unknown>>(
    () =>
      editor.kind === "json"
        ? (overlay ?? payload)
        : { ...payload, ...(overlay ?? {}) },
    [editor.kind, overlay, payload],
  );
  // `{}` is a valid JSON object, and `PUT /{entity}/enrich` is a full replace,
  // so approving one erases the entity's enrichment and marks the job done.
  // The editor rejects it too; this is the same gate read off the submission
  // rather than off the payload that arrived, because those differ the moment
  // the curator deletes the last key.
  const hasEnrichmentToWrite = Object.keys(editedEnrichment).length > 0;

  if (!problem) {
    // Unreachable: the registry keys this component on the reason that produces
    // the problem. Loud rather than a blank panel if the union ever drifts.
    return (
      <p className="text-sm text-destructive">
        This item is in enrichment review but carries no
        <code className="mx-1">enrichment_low_confidence</code> problem payload.
      </p>
    );
  }

  async function submit(
    resolution:
      | { kind: "enrichment.approve" }
      | { kind: "enrichment.replace"; enrichment: Record<string, unknown> }
      | { kind: "enrichment.retry" },
  ) {
    setSubmitting(true);
    try {
      await resolveHitl(
        item.id,
        { resolution },
        { navigateTo: ENRICHMENT_RESOURCE },
      );
      onResolved();
    } catch {
      // `resolveHitl` has already toasted the failure and captured it to
      // Sentry, then rethrown so a caller that wants to stay put can tell.
      // This one has nothing left to do: `finally` re-enables the buttons and
      // the curator can try again. Letting it escape the `void submit(...)`
      // call sites bought an unhandled rejection and a second, identical
      // Sentry event via the global handler — never a better error message.
    } finally {
      setSubmitting(false);
    }
  }

  function handleReplace() {
    void submit({ kind: "enrichment.replace", enrichment: editedEnrichment });
  }

  const parkCount = problem.park_count;
  const lastAttempt = isLastAttempt(parkCount);
  const entityLabel = enrichmentEntityLabel(entity);

  return (
    <div className="flex flex-col gap-4">
      {!hasProposal && (
        <p className="inline-flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            This park carries no proposal payload, so there is nothing to
            approve or edit. Re-run the enrichment, or force-close the job.
            {/* Older rows, and any park whose payload did not survive the
                jsonb round-trip. Approving a proposal that cannot be displayed
                is the one thing this UI must never allow. */}
          </span>
        </p>
      )}

      {guard.alreadyEnriched && (
        <p className="inline-flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            {entityLabel} <code>{problem.entity_id}</code> already reads{" "}
            <strong>Enriched</strong> — an enrichment was approved for it after
            this proposal parked. Approving, or saving edits, replaces that one
            in full, and there is no earlier version to go back to.
          </span>
        </p>
      )}

      {editor.kind === "json" && editor.notice && editing && (
        <p className="inline-flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{editor.notice}</span>
        </p>
      )}

      {editing && (
        <div className="flex flex-col gap-4 rounded-md border p-3">
          <ProposalEditor
            editor={editor}
            entity={entity}
            payload={payload}
            overlay={overlay}
            onEdit={setOverlay}
            onValidityChange={setJsonValid}
          />
          <Separator />
          <div className="flex flex-wrap items-center gap-2">
            <ApproveButton
              kind="replace"
              requiresConfirm={guard.alreadyEnriched}
              entityLabel={entityLabel}
              entityId={problem.entity_id}
              busy={submitting || guard.pending}
              disabled={
                submitting ||
                guard.pending ||
                !jsonValid ||
                !hasEnrichmentToWrite
              }
              onApprove={handleReplace}
            />
            <Button
              type="button"
              variant="ghost"
              disabled={submitting}
              onClick={() => {
                setOverlay(null);
                setJsonValid(true);
                setEditing(false);
              }}
            >
              <X className="mr-1 h-4 w-4" />
              Discard edits
            </Button>
            <span className="text-xs text-muted-foreground">
              Writes the edited proposal to {entityLabel.toLowerCase()}{" "}
              <code>{problem.entity_id}</code> and marks it Enriched.
            </span>
          </div>
        </div>
      )}

      {!editing && (
        <div className="flex flex-wrap items-center gap-2">
          <ApproveButton
            kind="approve"
            requiresConfirm={guard.alreadyEnriched}
            entityLabel={entityLabel}
            entityId={problem.entity_id}
            busy={submitting || guard.pending}
            // Approve sends no payload — Tina re-reads the proposal — so this
            // one is gated on the proposal that arrived, not on the overlay.
            disabled={submitting || guard.pending || !hasProposal}
            onApprove={() => void submit({ kind: "enrichment.approve" })}
          />
          <Button
            type="button"
            variant="outline"
            disabled={submitting || !hasProposal}
            onClick={() => setEditing(true)}
          >
            <Pencil className="mr-1 h-4 w-4" />
            Edit &amp; approve
          </Button>
          <RetryDialog
            attemptLabel={parkAttemptLabel(parkCount)}
            lastAttempt={lastAttempt}
            disabled={submitting}
            onConfirm={() => void submit({ kind: "enrichment.retry" })}
          />
        </div>
      )}

      {!editing && hasProposal && (
        <p className="text-xs text-muted-foreground">
          Approve writes this proposal to {entityLabel.toLowerCase()}{" "}
          <code>{problem.entity_id}</code> verbatim and marks it Enriched.
          Nothing is on its canonical fields until then.
        </p>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Approve, and the one case where it destroys something                      */
/* -------------------------------------------------------------------------- */

type OverwriteGuard = {
  /** The entity reads `Enriched` right now — approving destroys curator work. */
  alreadyEnriched: boolean;
  /** The read has not answered yet, which is not the same as answering "no". */
  pending: boolean;
};

/**
 * Whether the entity this proposal targets reads `Enriched` **right now**, and
 * whether that read has answered at all yet.
 *
 * `Enriched` is the only case in which approving loses work, and the reason the
 * confirm below is conditional rather than universal. Tina writes every parked
 * proposal onto the entity as `NeedsReview` *before* it parks, so an
 * ordinary approve only promotes what is already on the document — nothing is
 * overwritten and a confirm would be theatre. `Enriched` at this moment means
 * something else happened in between: a curator opened
 * `/{entity}/enrichment/:id` and saved by hand. `PUT /{entity}/enrich` is a
 * **full replace** and Firestore keeps no version history, so re-approving the
 * model's now-stale proposal over that is unrecoverable.
 *
 * Read live rather than off the park payload: the park is a snapshot from
 * before the curator's edit, and what happened *after* it is the entire
 * question.
 *
 * **Fails open on error; closed while the answer is outstanding.** Those are
 * different questions, and this hook used to answer both with `false`.
 *
 * - *Error* — 404, offline backend, a resource whose `getOne` omits the field —
 *   leaves approve exactly as one click. A confirm the panel cannot justify is
 *   worse than none: it trains the curator to click through the dialog, which
 *   is how the one that matters stops being read. Unchanged, deliberate.
 * - *In flight* — "not answered yet" is not "answered no". While the read is
 *   outstanding the panel cannot know whether this is the destructive case, so
 *   the caller disables approve rather than assuming the cheap answer. On a
 *   cold Mood backend that window is seconds wide, and for its whole width an
 *   already-`Enriched` entity got neither the banner nor the confirm.
 *
 * A query that never runs — an entity type with no Mood resource, a null id —
 * is not in flight: no answer is coming, so it fails open like an error instead
 * of disabling approve forever.
 */
function useEntityAlreadyEnriched(
  entity: string | null,
  entityId: string | null,
): OverwriteGuard {
  const { getOne } = useDataProvider()();
  const resource = isEnrichmentEntity(entity) ? ENTITY_RESOURCE[entity] : null;
  const enabled = Boolean(resource && entityId);

  const { data, isPending } = useQuery({
    queryKey: [resource, entityId, "enrichment-status"],
    queryFn: async () => {
      // This provider's `getOne` returns the flat record (it answers with
      // `response.data`), which is why every repository in the tree reads it
      // straight through — the declared `GetOneResponse` shape is the lie.
      const record = asRecord(
        await getOne({ id: entityId as string, resource: resource as string }),
      );
      return asString(record.enrichmentStatus) ?? null;
    },
    enabled,
    // One read, and a stale answer is the wrong direction to be stubborn in.
    staleTime: 30_000,
    retry: false,
  });

  return {
    alreadyEnriched: data === "Enriched",
    pending: enabled && isPending,
  };
}

/**
 * The two writes a curator can make from this panel, and the copy each needs.
 *
 * `approve` sends no payload — Tina re-reads the parked proposal — while
 * `replace` sends the edited one. The *consequence* is identical: one full
 * replace of the entity's stored enrichment. So they share one confirm, which
 * is the whole reason both go through {@link ApproveButton}.
 */
type ApproveKind = "approve" | "replace";

const APPROVE_COPY: Record<
  ApproveKind,
  { face: string; cta: string; clause: string }
> = {
  approve: {
    face: "Approve proposal",
    cta: "Overwrite and approve",
    clause:
      "Approving replaces the stored enrichment with this proposal in full",
  },
  replace: {
    face: "Approve & save edits",
    cta: "Overwrite and save edits",
    clause:
      "Saving these edits replaces the stored enrichment with the edited proposal in full",
  },
};

/**
 * A write to the entity, behind a confirm **only when it already reads
 * `Enriched`** (owner decision, 2026-08-27).
 *
 * The risk gradient used to run backwards: this button re-PUT the proposal to a
 * production document in one click, while "Re-run enrichment" — an LLM call,
 * fully reversible — asked permission. Every approve confirming would fix the
 * asymmetry the wrong way, because the overwhelming majority write over a
 * proposal Tina itself just put there. Only the case that actually loses
 * something asks.
 *
 * Both writes come through here rather than the edit branch growing a plain
 * `<Button>`. "Approve & save edits" overwrites exactly as hard as "Approve
 * proposal" does — the panel's own banner says so — and a second call site is
 * how that quietly stops being true.
 */
function ApproveButton({
  kind,
  requiresConfirm,
  entityLabel,
  entityId,
  busy,
  disabled,
  onApprove,
}: {
  kind: ApproveKind;
  requiresConfirm: boolean;
  entityLabel: string;
  entityId: string;
  /** Submitting, or still waiting on the overwrite guard's read. */
  busy: boolean;
  disabled: boolean;
  onApprove: () => void;
}) {
  const [open, setOpen] = useState(false);
  const copy = APPROVE_COPY[kind];
  const face = (
    <>
      {busy ? (
        <Loader2 className="mr-1 h-4 w-4 animate-spin" />
      ) : (
        <ThumbsUp className="mr-1 h-4 w-4" />
      )}
      {copy.face}
    </>
  );

  if (!requiresConfirm) {
    return (
      <Button type="button" disabled={disabled} onClick={onApprove}>
        {face}
      </Button>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" disabled={disabled}>
          {face}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Overwrite the enrichment already on this {entityLabel.toLowerCase()}
            ?
          </DialogTitle>
          <DialogDescription>
            {entityLabel} <code>{entityId}</code> reads{" "}
            <strong>Enriched</strong>, so someone approved an enrichment for it
            after this proposal parked. {copy.clause} — about text, SEO fields,
            feature scores, everything the write carries — and no earlier
            version is kept anywhere. Whatever that curator wrote is gone.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant="destructive"
            onClick={() => {
              setOpen(false);
              onApprove();
            }}
          >
            <ThumbsUp className="mr-1 h-4 w-4" />
            {copy.cta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* -------------------------------------------------------------------------- */
/* Which editor this entity gets                                              */
/* -------------------------------------------------------------------------- */

type EditorChoice =
  | { kind: "artist"; value: ArtistEnrichmentRead }
  | { kind: "venue"; value: VenueEnrichmentRead }
  | { kind: "json"; notice: string | null };

const DRIFT_NOTICE =
  "IMS could not read this proposal with its typed editor — the schema mirror " +
  "has drifted from what Tina wrote. Editing as raw JSON so the queue is not " +
  "stranded; the mirror is the bug, not this proposal.";

const UNEXPECTED_ENTITY_NOTICE =
  "This entity type is not supposed to reach the confidence gate. Editing as " +
  "raw JSON — worth reporting, because a gated flow gained an entity IMS does " +
  "not have a typed editor for.";

/**
 * The per-entity editor mapping, with its drift fallback.
 *
 * `safeParse`, never `.parse()`: a proposal Tina wrote but IMS's Zod mirror
 * rejects is a mirror-drift bug, and stranding the queue over one is worse than
 * showing the curator raw JSON with a notice.
 *
 * `event` / `genre` / `screening` / `geodata` / `performance` are ungated and
 * cannot appear here, so they get no branch — they fall through to the
 * assert-and-fallback arm if one ever does. `performance` is ungated exactly
 * as `screening` is (`tina-enrichment` SKILL.md: "ungated exactly as
 * screening is"); `play` IS gated (0.5, movie's threshold — MOO-4814 task
 * 17), so it joins the movie/person/show_genre branch below despite having no
 * typed editor either.
 */
function useEditorChoice(
  entity: string | null,
  payload: Record<string, unknown>,
  hasProposal: boolean,
): EditorChoice {
  return useMemo<EditorChoice>(() => {
    if (!hasProposal) return { kind: "json", notice: null };
    if (entity === "artist") {
      const parsed = artistEnrichmentReadSchema.safeParse(payload);
      return parsed.success
        ? { kind: "artist", value: parsed.data }
        : { kind: "json", notice: DRIFT_NOTICE };
    }
    if (entity === "venue") {
      const parsed = venueEnrichmentReadSchema.safeParse(payload);
      return parsed.success
        ? { kind: "venue", value: parsed.data }
        : { kind: "json", notice: DRIFT_NOTICE };
    }
    // The four gated entities with no typed editor anywhere, which is exactly
    // why `JsonEnrichmentEditor` exists. `play` (MOO-4814 task 17) is gated
    // like movie/person/show_genre but, like them, never got one.
    if (
      entity === "movie" ||
      entity === "person" ||
      entity === "show_genre" ||
      entity === "play"
    ) {
      return { kind: "json", notice: null };
    }
    return { kind: "json", notice: UNEXPECTED_ENTITY_NOTICE };
  }, [entity, payload, hasProposal]);
}

function ProposalEditor({
  editor,
  entity,
  payload,
  overlay,
  onEdit,
  onValidityChange,
}: {
  editor: EditorChoice;
  entity: string | null;
  payload: Record<string, unknown>;
  overlay: Record<string, unknown> | null;
  onEdit: (next: Record<string, unknown>) => void;
  onValidityChange: (isValid: boolean) => void;
}) {
  return (
    <Suspense fallback={<EditorLoading />}>
      {editor.kind === "artist" ? (
        <EnrichmentEditor
          config={enrichmentConfigFor("artists")}
          enrichment={{ ...editor.value, ...(overlay ?? {}) }}
          updateEnrichment={onEdit}
        />
      ) : editor.kind === "venue" ? (
        <VenueEnrichmentResult
          enrichment={
            { ...editor.value, ...(overlay ?? {}) } as VenueEnrichmentRead
          }
          updateEnrichment={(values) =>
            onEdit(values as unknown as Record<string, unknown>)
          }
        />
      ) : (
        <div className="flex flex-col gap-4">
          <ReadOnlyProposalCards entity={entity} payload={payload} />
          <JsonEnrichmentEditor
            value={payload}
            onChange={onEdit}
            onValidityChange={onValidityChange}
          />
        </div>
      )}
    </Suspense>
  );
}

function EditorLoading() {
  return (
    <p className="inline-flex items-center gap-2 py-8 text-sm text-muted-foreground">
      <Loader2 className="h-4 w-4 animate-spin" />
      Loading the editor…
    </p>
  );
}

/**
 * The "read the decision, don't read raw JSON to make it" half.
 *
 * The same presentational blocks the movie/person/show-genre show pages use,
 * rendered above the editor so the curator judges the proposal from formatted
 * cards and only drops into JSON to change it.
 *
 * `play` (MOO-4814 task 17) reaches this component too — {@link useEditorChoice}
 * routes it here alongside movie/person/show_genre — but it is deliberately
 * absent from `labels` below: `play.enrich.enrich`'s decision carries only
 * `confidence`/`reasoning`, no `features` member (`tina-enrichment` SKILL.md's
 * enrich-arm table), and `PlayAIEnrichmentDbDto` has no `Features` property
 * either. `ReasoningBlock`/`EnrichmentSection`'s `confidence` and
 * `SeoGeoBlock`/`TransliterationsBlock` below still render for it.
 */
function ReadOnlyProposalCards({
  entity,
  payload,
}: {
  entity: string | null;
  payload: Record<string, unknown>;
}) {
  // No `play` arm: it has no `features` member to show (see the docstring).
  const labels =
    entity === "movie"
      ? MOVIE_FEATURE_LABELS
      : entity === "person"
        ? PERSON_FEATURE_LABELS
        : entity === "show_genre"
          ? SHOW_GENRE_FEATURE_LABELS
          : null;

  const reasoning = payload.reasoning;
  const reasoningText =
    typeof reasoning === "string"
      ? reasoning
      : typeof (reasoning as { text?: unknown } | null)?.text === "string"
        ? ((reasoning as { text: string }).text ?? null)
        : null;

  return (
    <EnrichmentSection
      confidence={
        typeof payload.confidence === "number" ? payload.confidence : undefined
      }
    >
      <ReasoningBlock text={reasoningText} />
      <SeoGeoBlock seo={payload as ComponentProps<typeof SeoGeoBlock>["seo"]} />
      <TransliterationsBlock
        transliterations={
          payload.transliterations as ComponentProps<
            typeof TransliterationsBlock
          >["transliterations"]
        }
      />
      {labels && (
        <FeatureScores
          features={
            payload.features as Record<string, unknown> | null | undefined
          }
          labels={labels}
        />
      )}
    </EnrichmentSection>
  );
}

/* -------------------------------------------------------------------------- */
/* Re-run                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * The retry confirm, which states the cost.
 *
 * **Deliberately not disabled on the last attempt.** Tina caps parks itself
 * (`park_count >= TINA_ENRICH_HITL_MAX_PARKS` closes the job for manual
 * review), and a third park can *only* come from a curator retry — so disabling
 * the button one attempt early makes `park_limit_reached`, and the entire
 * park-limit notice, unreachable. The copy escalates instead.
 */
function RetryDialog({
  attemptLabel,
  lastAttempt,
  disabled,
  onConfirm,
}: {
  attemptLabel: string;
  lastAttempt: boolean;
  disabled: boolean;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled}>
          <RotateCcw className="mr-1 h-4 w-4" />
          Re-run enrichment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Re-run enrichment</DialogTitle>
          <DialogDescription>
            Re-extracts from scratch and calls the model again — this costs a
            full enrichment. {attemptLabel}.{" "}
            {lastAttempt
              ? `This is the last of ${ENRICHMENT_PARK_LIMIT} — another low-confidence result closes the job for manual review instead of parking again.`
              : "A low-confidence result parks it again for review."}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => {
              setOpen(false);
              onConfirm();
            }}
          >
            <RotateCcw className="mr-1 h-4 w-4" />
            Re-run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
